import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Share2,
  Printer,
  FileDown,
  CheckCircle2,
  MapPin,
  Phone,
  MessageCircle,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  api,
  getDayPlan,
  upsertDayPlan,
  lookupCustomerByNumber,
  getAppSettings,
  getDayPlanPdfBlob,
} from "@/lib/api";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()}`;
};

const blankItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  customer_number: "",
  customer_name: "",
  time: "",
  phone: "",
  address: "",
  type: "",
  amount: "",
  fx: "",
  sl: "",
  p: "",
  pd: "",
  plis: "",
  fr: "",
  status: "",
  notes: "",
});

const QTY_COLS = [
  { key: "fx", label: "Fx", title: "Fixed screen" },
  { key: "sl", label: "Sl", title: "Sliding screen" },
  { key: "p", label: "P", title: "Patio door screen" },
  { key: "pd", label: "PD", title: "Pulldown screen" },
  { key: "plis", label: "Plis", title: "Pleated screen" },
  { key: "fr", label: "Fr", title: "Frame / tracks" },
];

const DayPlanDetail = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const dirtyRef = useRef(false);
  // Always-fresh refs so the unmount/visibility hook can flush latest state
  // without re-binding on every keystroke.
  const planRef = useRef(null);
  const dateRef = useRef(date);
  const saveTimerRef = useRef(null);
  useEffect(() => {
    planRef.current = plan;
  }, [plan]);
  useEffect(() => {
    dateRef.current = date;
  }, [date]);

  // Auto-resize a <textarea> to match its content. Used for the address
  // cell so pressing Enter wraps to a 2nd line that's actually visible.
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  useEffect(() => {
    Promise.all([getDayPlan(date), getAppSettings()])
      .then(([p, s]) => {
        setPlan(p);
        setSettings(s || {});
      })
      .catch(() => toast.error("Failed to load day plan"))
      .finally(() => setLoading(false));
  }, [date]);

  // Open the latest quote's CUT SHEET for the given customer-number row.
  // Used by the workshop hands when they tap a customer name on the day plan.
  const openJobCutSheet = async (item) => {
    const num = (item.customer_number || "").toString().trim();
    if (!num) {
      toast.info(
        "No MN-XXX number on this row yet. Type it in first or fill the customer name and link it."
      );
      return;
    }
    try {
      // Need the customer's client_name to fetch their quote list.
      const dir = await lookupCustomerByNumber(num);
      const name = dir?.name;
      if (!name) {
        toast.error(`MN-${num.padStart(3, "0")} not found`);
        return;
      }
      const { data: quotes } = await api.get(
        `/customers/${encodeURIComponent(name)}`
      );
      if (!quotes || quotes.length === 0) {
        toast.error("No quotes found for this customer");
        return;
      }
      // Most recent first (server already sorts desc by created_at).
      const targetId = quotes[0].id;
      navigate(`/quotes/${targetId}#cut`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't open cut sheet";
      toast.error(msg);
    }
  };

  const setItems = (updater) => {
    dirtyRef.current = true;
    setPlan((prev) => ({ ...prev, items: updater(prev.items || []) }));
    scheduleAutoSave();
  };

  // Debounced auto-save — runs 1.5s after the last edit. Keeps the partner's
  // schedule safe if she taps away to /quotes to look up a customer number.
  const scheduleAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushSave({ silent: true });
    }, 1500);
  };

  // One-shot save used by both the manual button and the auto-save timer.
  // `silent=true` skips the success toast (no spam during typing).
  const flushSave = async (opts = {}) => {
    const current = planRef.current;
    if (!current || !dirtyRef.current) return;
    setSaving(true);
    try {
      const fresh = await upsertDayPlan(dateRef.current, {
        items: current.items || [],
        notes: current.notes || "",
      });
      setPlan(fresh);
      dirtyRef.current = false;
      if (!opts.silent) toast.success("Saved");
    } catch {
      if (!opts.silent) toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Save-on-leave safety net: flush pending changes when navigating away
  // (route change → cleanup), tab/window close, or tab hidden (mobile switch).
  useEffect(() => {
    const flushNow = () => {
      if (!dirtyRef.current || !planRef.current) return;
      const payload = {
        items: planRef.current.items || [],
        notes: planRef.current.notes || "",
      };
      // Use sendBeacon for the most reliable flush during page-unload, falling
      // back to the regular API when not available.
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/day-plans/${encodeURIComponent(dateRef.current)}`;
        const token = localStorage.getItem("screen_app_token") || "";
        const blob = new Blob(
          [JSON.stringify({ ...payload, _auth: token })],
          { type: "application/json" }
        );
        const ok = navigator.sendBeacon && navigator.sendBeacon(url, blob);
        if (!ok) {
          // Fallback fires-and-forgets the regular API call
          upsertDayPlan(dateRef.current, payload).catch(() => {});
        }
      } catch {
        upsertDayPlan(dateRef.current, payload).catch(() => {});
      }
      dirtyRef.current = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushNow();
    };
    window.addEventListener("beforeunload", flushNow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", flushNow);
      document.removeEventListener("visibilitychange", onVisibility);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Final flush on route change away from this Day Plan
      flushNow();
    };
  }, []);

  const updateItem = (id, patch) => {
    setItems((items) => items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    setItems((items) => items.filter((it) => it.id !== id));
  };

  const addItem = () => {
    setItems((items) => [...items, blankItem()]);
  };

  // Move a row up or down in the list. Used by the per-row arrow buttons so
  // the user can re-sort the day's appointments without dragging.
  const moveItem = (id, direction) => {
    setItems((items) => {
      const idx = items.findIndex((it) => it.id === id);
      if (idx < 0) return items;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= items.length) return items;
      const next = items.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // Look up MN-XXX → pre-fill name/phone/address
  const handleNumberBlur = async (item) => {
    const raw = (item.customer_number || "").trim();
    if (!raw) return;
    // Strip whatever prefix the user might have typed and re-format.
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) {
      // They typed something other than digits — clear the field
      updateItem(item.id, { customer_number: "" });
      return;
    }
    const label = `MN-${digits.padStart(3, "0")}`;
    try {
      const entry = await lookupCustomerByNumber(label);
      const next = {
        customer_number: entry.customer_number_label,
      };
      if (!item.customer_name) next.customer_name = entry.name;
      if (!item.phone && entry.phone) next.phone = entry.phone;
      if (!item.address) {
        // Prefer the full street address; fall back to the area label
        next.address = entry.default_address || entry.default_area || "";
      }
      updateItem(item.id, next);
      toast.success(`Loaded ${entry.customer_number_label} ${entry.name}`);
    } catch {
      // Keep the formatted label so user sees what was attempted
      updateItem(item.id, { customer_number: label });
      toast.error(`No customer found for "${label}"`);
    }
  };

  // Strip the MN- prefix for editing — the visual prefix renders separately.
  const cnDigits = (s) => (s || "").replace(/[^0-9]/gi, "");

  const save = async () => {
    // Force-mark dirty so a manual click always re-saves, even if auto-save
    // already flushed everything.
    dirtyRef.current = true;
    await flushSave({ silent: false });
  };

  // Build a clean WhatsApp-friendly text version of the day
  const buildShareText = () => {
    const head = `*${(settings.business_name || "").trim() || "MosquitoNick"}*\n*Schedule — ${fmtDate(date)}*\n`;
    const lines = [head];
    (plan?.items || []).forEach((it, i) => {
      const num = it.customer_number ? `${it.customer_number} ` : "";
      const time = it.time ? `${it.time}  ` : "";
      const nameBit = `${num}${it.customer_name || "(no name)"}`.trim();
      lines.push(`${i + 1}. ${time}${nameBit}`);
      if (it.phone) lines.push(`   ${it.phone}`);
      if (it.address) lines.push(`   ${it.address}`);
      if (it.type) lines.push(`   ${it.type}`);
      const qtys = QTY_COLS.map((c) =>
        it[c.key] ? `${c.label} ${it[c.key]}` : null
      ).filter(Boolean);
      if (qtys.length) lines.push(`   ${qtys.join(" · ")}`);
      if (it.amount) lines.push(`   ${it.amount}`);
      if (it.notes) lines.push(`   📝 ${it.notes}`);
      lines.push("");
    });
    if ((plan?.notes || "").trim()) {
      lines.push("---");
      lines.push(plan.notes);
    }
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const text = buildShareText();
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadPdf = async () => {
    // Persist any unsaved edits first so the PDF reflects what's on screen.
    if (dirtyRef.current) {
      try {
        await save();
      } catch {
        toast.error("Save the plan first, then try the PDF");
        return;
      }
    }
    try {
      const blob = await getDayPlanPdfBlob(date);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `day_plan_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a moment so iOS Safari has time to hand the file off.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const totalJobs = useMemo(() => (plan?.items || []).length, [plan]);

  if (loading) {
    return (
      <div className="p-10 font-mono text-xs text-zinc-400 uppercase tracking-[0.18em]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="day-plan-page">
      {/* Print orientation: landscape A4 so the wide table fits comfortably.
          Scoped to this page only via a component-local <style> element — when
          you tap Print here, only this view is in the print output, so the
          rule doesn't leak into other pages. */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          /* Keep rows together — never split a customer's row across pages */
          [data-testid^="day-plan-item-"] {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Force the table to fit the page width — no horizontal overflow */
          [data-testid="day-plan-table"] {
            font-size: 9pt;
            table-layout: fixed;
            width: 100% !important;
          }
          /* Allow long addresses / names to wrap rather than push columns off-page */
          [data-testid="day-plan-table"] td,
          [data-testid="day-plan-table"] th {
            padding: 3px 4px !important;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          [data-testid="day-plan-table"] input,
          [data-testid="day-plan-table"] textarea {
            border: none !important;
            padding: 0 !important;
            font-size: 9pt !important;
            background: transparent !important;
            width: 100% !important;
            resize: none !important;
            overflow: visible !important;
          }
          /* Repeat the header on every printed page */
          [data-testid="day-plan-table"] thead {
            display: table-header-group;
          }
          /* Print the bold grid (matches the on-screen look) */
          [data-testid="day-plan-table"],
          [data-testid="day-plan-table"] th,
          [data-testid="day-plan-table"] td {
            border: 0.6pt solid #09090b !important;
          }
          [data-testid="day-plan-table"] thead tr {
            border-bottom: 1.2pt solid #09090b !important;
          }
          /* Kill horizontal scroll wrapper in print */
          [data-testid="day-plan-table-wrap"] {
            overflow: visible !important;
            border: none !important;
          }
          /* Hide the trash column entirely on print */
          [data-testid="day-plan-table"] td:last-child,
          [data-testid="day-plan-table"] th:last-child { display: none; }
        }
      `}</style>
      {/* Sticky bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-zinc-200 print:hidden">
        <div className="px-6 md:px-10 py-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate("/day-plans")}
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#09090b]"
            data-testid="back-to-day-plans"
          >
            <ArrowLeft size={14} /> All plans
          </button>
          <div className="flex-1" />
          <button
            onClick={shareWhatsApp}
            className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1fb955] text-white px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
            data-testid="day-plan-whatsapp-btn"
          >
            <Share2 size={12} /> WhatsApp
          </button>
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 bg-white border border-[#09090b] hover:bg-zinc-100 px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
            data-testid="day-plan-pdf-btn"
          >
            <FileDown size={12} /> Save PDF
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 bg-white border border-[#09090b] hover:bg-zinc-100 px-3 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
            data-testid="day-plan-print-btn"
          >
            <Printer size={12} /> Print
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-[#09090b] hover:bg-[#27272a] disabled:opacity-60 text-white px-4 h-9 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors"
            data-testid="day-plan-save-btn"
          >
            {saving ? <CheckCircle2 size={12} /> : <Save size={12} />}
            {saving ? "Saving…" : dirtyRef.current ? "Save" : "Saved"}
          </button>
        </div>
      </div>

      <div className="p-4 md:p-6">
        <div className="label-xs">Day plan</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-[0.95] mt-1">
          {fmtDate(date)}
        </h1>
        <div className="font-mono text-xs text-zinc-500 mt-2">
          {totalJobs} job{totalJobs === 1 ? "" : "s"} on this day · Auto-saves as you type
        </div>

        {/* Legend */}
        <div className="mt-6 mb-6 inline-flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-zinc-500 border border-zinc-200 px-3 py-2">
          {QTY_COLS.map((c) => (
            <span key={c.key}>
              <span className="font-bold text-[#09090b]">{c.label}</span> {c.title}
            </span>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto border border-zinc-700" data-testid="day-plan-table-wrap">
          <table
            className="w-full text-sm border-collapse [&_th]:border-r [&_th]:border-zinc-700 [&_td]:border-r [&_td]:border-zinc-700 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0"
            data-testid="day-plan-table"
          >
            <colgroup>
              <col style={{ width: "6%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "1%" }} />
            </colgroup>
            <thead className="bg-[#f4f4f5] border-b-2 border-zinc-800">
              <tr>
                <th className="text-left p-1.5 label-xs">No.</th>
                <th className="text-left p-1.5 label-xs">Time</th>
                <th className="text-left p-1.5 label-xs">Name</th>
                <th className="text-left p-1.5 label-xs">Tel</th>
                <th className="text-left p-1.5 label-xs">Address</th>
                <th className="text-left p-1.5 label-xs">Type / job</th>
                <th className="text-left p-1.5 label-xs">Amount</th>
                {QTY_COLS.map((c) => (
                  <th
                    key={c.key}
                    className="text-center p-1.5 label-xs"
                    title={c.title}
                  >
                    {c.label}
                  </th>
                ))}
                <th className="text-left p-1.5 label-xs">·</th>
              </tr>
            </thead>
            <tbody>
              {(plan.items || []).map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-zinc-700 align-top"
                  data-testid={`day-plan-item-${it.id}`}
                >
                  <td className="p-0">
                    <div
                      className="flex items-stretch h-full focus-within:bg-zinc-50"
                      data-testid={`item-num-wrap-${it.id}`}
                    >
                      <span className="bg-zinc-100 border-r border-zinc-700 px-1 inline-flex items-center font-mono text-[9px] font-bold text-zinc-500 select-none">
                        MN-
                      </span>
                      <input
                        value={cnDigits(it.customer_number)}
                        onChange={(e) =>
                          updateItem(it.id, {
                            customer_number: e.target.value
                              .replace(/[^0-9]/g, "")
                              .slice(0, 4),
                          })
                        }
                        onBlur={() => handleNumberBlur(it)}
                        placeholder="001"
                        inputMode="numeric"
                        className="flex-1 min-w-0 px-1 py-1.5 font-mono text-xs tabular-nums border-0 focus:outline-none bg-transparent"
                        data-testid={`item-num-${it.id}`}
                      />
                    </div>
                  </td>
                  <td className="p-0">
                    <input
                      value={it.time}
                      onChange={(e) => updateItem(it.id, { time: e.target.value })}
                      placeholder="9-11 am"
                      className="w-full border-0 px-1 py-1.5 font-mono text-xs focus:outline-none focus:bg-zinc-50"
                    />
                  </td>
                  <td className="p-0">
                    {(it.customer_number || "").toString().trim() ? (
                      <button
                        type="button"
                        onClick={() => openJobCutSheet(it)}
                        className="block w-full text-left px-1 py-1.5 text-sm text-[#005bea] hover:bg-blue-50 underline-offset-2 hover:underline focus:outline-none focus:bg-blue-50"
                        title="Open this job's cut sheet"
                        data-testid={`item-name-link-${it.id}`}
                      >
                        {it.customer_name || (
                          <span className="text-zinc-400 italic">No name</span>
                        )}
                      </button>
                    ) : (
                      <input
                        value={it.customer_name}
                        onChange={(e) =>
                          updateItem(it.id, { customer_name: e.target.value })
                        }
                        placeholder="Name"
                        className="w-full border-0 px-1 py-1.5 text-sm focus:outline-none focus:bg-zinc-50"
                      />
                    )}
                  </td>
                  <td className="p-0">
                    <div className="flex items-stretch h-full">
                      <input
                        value={it.phone}
                        onChange={(e) => updateItem(it.id, { phone: e.target.value })}
                        placeholder="+34..."
                        inputMode="tel"
                        className="flex-1 min-w-0 border-0 px-1 py-1.5 font-mono text-xs tabular-nums focus:outline-none focus:bg-zinc-50 bg-transparent"
                      />
                      {(it.phone || "").trim() ? (
                        <>
                          <a
                            href={`tel:${(it.phone || "").replace(/[^0-9+]/g, "")}`}
                            className="inline-flex items-center justify-center w-6 border-l border-zinc-700 text-zinc-500 hover:text-[#09090b] print:hidden"
                            title="Call"
                            data-testid={`item-phone-call-${it.id}`}
                          >
                            <Phone size={12} />
                          </a>
                          <a
                            href={`https://wa.me/${(it.phone || "").replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-6 border-l border-zinc-700 text-zinc-500 hover:text-[#25D366] print:hidden"
                            title="Open in WhatsApp"
                            data-testid={`item-phone-wa-${it.id}`}
                          >
                            <MessageCircle size={12} />
                          </a>
                        </>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-0">
                    <div className="flex items-stretch h-full">
                      <textarea
                        rows={1}
                        ref={autoGrow}
                        onInput={(e) => autoGrow(e.currentTarget)}
                        value={it.address}
                        onChange={(e) =>
                          updateItem(it.id, { address: e.target.value })
                        }
                        placeholder="Urb. … C/…"
                        className="flex-1 min-w-0 border-0 px-1 py-1.5 text-xs focus:outline-none focus:bg-zinc-50 resize-none overflow-hidden leading-tight bg-transparent"
                        data-testid={`item-address-${it.id}`}
                      />
                      {(it.address || "").trim() ? (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(it.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-6 border-l border-zinc-700 text-zinc-500 hover:text-[#005bea] print:hidden"
                          title="Open in Google Maps"
                          data-testid={`item-address-maps-${it.id}`}
                        >
                          <MapPin size={13} />
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-0">
                    <input
                      value={it.type}
                      onChange={(e) => updateItem(it.id, { type: e.target.value })}
                      placeholder="R + M new screen"
                      className="w-full border-0 px-1 py-1.5 text-xs focus:outline-none focus:bg-zinc-50"
                    />
                  </td>
                  <td className="p-0">
                    <input
                      value={it.amount}
                      onChange={(e) =>
                        updateItem(it.id, { amount: e.target.value })
                      }
                      placeholder="€"
                      className="w-full border-0 px-1 py-1.5 font-mono text-xs tabular-nums focus:outline-none focus:bg-zinc-50"
                    />
                  </td>
                  {QTY_COLS.map((c) => (
                    <td className="p-0 text-center" key={c.key}>
                      <input
                        value={it[c.key]}
                        onChange={(e) =>
                          updateItem(it.id, { [c.key]: e.target.value })
                        }
                        className="w-full text-center border-0 px-0.5 py-1.5 font-mono text-xs focus:outline-none focus:bg-zinc-50"
                      />
                    </td>
                  ))}
                  <td className="p-0 text-center">
                    <div className="flex flex-col items-stretch h-full print:hidden">
                      <button
                        type="button"
                        onClick={() => moveItem(it.id, "up")}
                        className="flex-1 inline-flex items-center justify-center text-zinc-400 hover:text-[#09090b] border-b border-zinc-200 leading-none"
                        title="Move up"
                        data-testid={`move-up-${it.id}`}
                      >
                        <ChevronUp size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(it.id, "down")}
                        className="flex-1 inline-flex items-center justify-center text-zinc-400 hover:text-[#09090b] border-b border-zinc-200 leading-none"
                        title="Move down"
                        data-testid={`move-down-${it.id}`}
                      >
                        <ChevronDown size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm("Remove this row?")) removeItem(it.id);
                        }}
                        className="flex-1 inline-flex items-center justify-center text-zinc-400 hover:text-red-600 leading-none"
                        data-testid={`remove-item-${it.id}`}
                        title="Remove row"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add row button */}
        <button
          type="button"
          onClick={addItem}
          className="mt-4 inline-flex items-center gap-1.5 border border-dashed border-zinc-400 hover:border-[#09090b] hover:bg-zinc-50 px-4 h-10 text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors print:hidden"
          data-testid="add-day-plan-item"
        >
          <Plus size={13} /> Add row
        </button>

        {/* Day notes */}
        <div className="mt-10 max-w-2xl">
          <div className="label-xs mb-1">Day notes</div>
          <textarea
            rows={4}
            value={plan.notes || ""}
            onChange={(e) => {
              dirtyRef.current = true;
              setPlan((p) => ({ ...p, notes: e.target.value }));
              scheduleAutoSave();
            }}
            placeholder="Anything for the day — route, weather, materials needed…"
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-[#09090b]"
            data-testid="day-plan-notes"
          />
        </div>

        {/* Footer save */}
        <div className="mt-6 print:hidden">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#09090b] hover:bg-[#27272a] disabled:opacity-60 text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save day plan"}
          </button>
          <Link
            to="/day-plans"
            className="ml-3 text-xs text-zinc-500 hover:text-[#09090b]"
          >
            Back to all plans
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DayPlanDetail;
